import { Add, Edit } from "@mui/icons-material";
import { Button, IconButton, Switch, Tooltip } from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";

const HolidayMaster = () => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);

    // Filter form
    const filterFormik = useFormik({
        initialValues: { search: "", start_date: "", end_date: "" },
        enableReinitialize: true,
    });

    const { data: holidayData, isLoading: loading } = useQuery(
        ["get_holiday_type", page, filterFormik.values],
        () =>
            axiosInstance.get(API_URLS.get_holiday_type, {
                params: {
                    search: filterFormik.values.search,
                    start_date: filterFormik.values.start_date,
                    end_date: filterFormik.values.end_date,
                    page,
                    count: 10,
                },
            }),
        { refetchOnWindowFocus: false }
    );

    const holidayList = holidayData?.data?.response || [];

    const fk = useFormik({
        initialValues: {
            name: "",
            date: "",
            category: "Other",
            description: "N/A",
            status: "1",
        }
        ,
        onSubmit: async (values) => {
            if (editData) return updateholiday(values);
            return createholiday(values);
        },
    });

    // CREATE
    const createholiday = async (values) => {
        try {
            const reqBody = {
                m11_hl_name: values.name,
                m11_hl_date: values.date,
                m11_hl_category: values.category,
                m11_hl_desc: values.description,
                m11_hl_status: 1,
            };

            const res = await axiosInstance.post(API_URLS.create_holiday_type, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_holiday_type");
                handleClose();
            }
        } catch {
            toast.error("Failed to create holiday");
        }
    };


    // UPDATE
    const updateholiday = async (values) => {
        try {
            const reqBody = {
                m11_hl_id: editData.m11_hl_id,
                m11_hl_name: values.name,
                m11_hl_date: values.date,
                m11_hl_category: values.category,
                m11_hl_desc: values.description,
                m11_hl_status: values.status,
            };

            const res = await axiosInstance.post(API_URLS.update_holiday_type, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_holiday_type");
                handleClose();
            }
        } catch {
            toast.error("Failed to update holiday");
        }
    };


    // STATUS TOGGLE
    const toggleStatus = async (row, checked) => {
        try {
            const reqBody = {
                m11_hl_id: row.m11_hl_id,
                // m11_hl_status: checked ? 1 : 0,
            };
            const res = await axiosInstance.post(API_URLS.status_holiday_type, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success)
                queryClient.invalidateQueries("get_holiday_type");
        } catch {
            toast.error("Failed to update status");
        }
    };


    const handleExport = async () => {
        try {
            const res = await axiosInstance.get(API_URLS.excel_holiday_type, {
                search: filterFormik.values.search || "",
                start_date: filterFormik.values.start_date || "",
                end_date: filterFormik.values.end_date || "",
            })
            const fileUrl = res?.data?.response?.url;
            window.open(fileUrl, "_blank")
        }
        catch (e) {
            console.log("Something went wrong")
        }
    }

    // Dialog control
    const handleOpen = () => {
        fk.resetForm();
        setEditData(null);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        fk.setValues({
            name: row.m11_hl_name,
            description: row.m11_hl_desc,
            date: moment(row.m11_hl_date).format("YYYY-MM-DD"),
        });
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    // TABLE HEADERS
    const tableHead = ["S.No", "Holiday Name", "Description", "Status", "Actions", "Created Date"];

    const tableRows = holidayList?.data?.map((item, index) => [
        index + 1,
        item.m11_hl_name || "--",
        item.m11_hl_desc || "--",
        <Switch
            checked={item.m11_hl_status === 1}
            onChange={(e) => toggleStatus(item, e.target.checked)}
            color="success"
        />,
        <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(item)}>
                <Edit className="text-blue-600" />
            </IconButton>
        </Tooltip>,
        moment(item.m11_hl_date).format("DD-MM-YYYY"),
    ]);


    return (
        <div className="mx-5">
            <div className="mb-5 flex justify-between items-center p-1 ">
                <p className="font-bold text-xl">Holiday Master</p>
                <Button variant="contained" className="!bg-[#00008b]" startIcon={<Add />} onClick={handleOpen}>
                    Add 
                </Button>
            </div>

            <CustomFilter
                formik={filterFormik}
                onFilter={() => {
                    setPage(1);
                    queryClient.invalidateQueries("get_holiday_type");
                }}
                onClear={() => {
                    filterFormik.resetForm();
                    setPage(1);
                    queryClient.invalidateQueries("get_holiday_type");
                }}
                onExport={handleExport}
            />

            <CustomTable tablehead={tableHead} tablerow={tableRows} isLoading={loading} />

            <CustomToPagination setPage={setPage} page={page} data={holidayList} />

            <CustomDialog
                open={open}
                onClose={handleClose}
                onSubmit={fk.handleSubmit}
                title={editData ? "Edit Holiday" : "Add Holiday"}
                formik={fk}
                fields={[
                    { name: "name", label: "Holiday Name", type: "text" },
                    { name: "date", label: "Holiday Date", type: "date" },
                    { name: "description", label: "Description", type: "text" },
                ]}

            />
        </div>
    );
};

export default HolidayMaster;
